import { useEffect, useState } from "react";
import { getGIWLOnlineQuote } from "services/onlineApplicationQuoteService";
import { zipRegex } from "constants/validations";

type UseGIWLQuoteParams = {
    zipCode?: string;
    gender?: "male" | "female";
    dobMonth?: string;
    dobDay?: string;
    dobYear?: string;
    isMember?: boolean;
    /** When false, the quote request is skipped (e.g. while the form has validation errors). Defaults to true. */
    enabled?: boolean;
};

export type GIWLCoverageOption = {
    coverageAmount: number;
    monthlyPremium: number;
};

const isCompleteDob = (month?: string, day?: string, year?: string) => !!month && !!day && !!year && year.length === 4;

/** A valid zip is a full 5-digit value, so partial entries (e.g. "1", "12") never trigger a request. */
const isValidZip = (zip?: string) => !!zip && zipRegex.test(zip);

export const useGIWLQuote = ({ zipCode, gender, dobMonth, dobDay, dobYear, isMember, enabled = true }: UseGIWLQuoteParams) => {
    const [coverageOptions, setCoverageOptions] = useState<GIWLCoverageOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // True when a request completed successfully but returned zero usable coverage options
    // (an empty/exhausted result that isn't a thrown error). Drives the "unable to find quotes" UI.
    const [noResults, setNoResults] = useState(false);

    useEffect(() => {
        if (!enabled || !isValidZip(zipCode) || !gender || !isCompleteDob(dobMonth, dobDay, dobYear)) {
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setError(null);
        setNoResults(false);
        // Clear prior options up front so a changed input never shows stale coverage while the new quote loads.
        setCoverageOptions([]);

        getGIWLOnlineQuote({
            zipCode,
            gender: gender === "male" ? "M" : "F",
            month: dobMonth!,
            day: dobDay!,
            year: dobYear!,
            isAAAMember: !!isMember,
        })
            .then((response) => {
                if (cancelled) return;
                const options: GIWLCoverageOption[] = (response.dataList ?? [])
                    .filter(
                        (item): item is { basePlan?: string; baseAmount: number; monthlyPremium: number } =>
                            item.baseAmount != null && item.monthlyPremium != null
                    )
                    .map((item) => ({
                        coverageAmount: item.baseAmount,
                        monthlyPremium: item.monthlyPremium,
                    }));
                setCoverageOptions(options);
                setNoResults(options.length === 0);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : "Failed to fetch quote");
                setCoverageOptions([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [enabled, zipCode, gender, dobMonth, dobDay, dobYear, isMember]);

    return { coverageOptions, isLoading, error, noResults };
};
